import React from 'react';
import {
  Container,
  Section,
  Grid,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Text,
  Stack,
  Badge,
  List,
  ListItem,
  Button
} from '@tonic/shared/components/ui';
import { AreaChart, DonutChart, BarChart } from '@tremor/react';
import { Leaf, Recycle, Droplet, Zap } from 'lucide-react';

export function SustainabilityMetricsPage() {
  // Placeholder data - would be fetched from API
  const summaryData = {
    carbonOffset: 12500,
    wasteDiverted: 8900,
    itemsRecycled: 3450,
    refillCount: 12800,
    waterSaved: 45000,
    energySaved: 28000
  };

  const carbonSavingsData = [
    { date: '2024-01', savings: 1800 },
    { date: '2024-02', savings: 2200 },
    { date: '2024-03', savings: 2500 }
  ];

  const materialBreakdownData = [
    { name: 'Plastic', value: 45 },
    { name: 'Textiles', value: 25 },
    { name: 'Electronics', value: 15 },
    { name: 'Metal', value: 10 },
    { name: 'Other', value: 5 }
  ];

  const dispositionData = [
    { name: 'Recycled', value: 60 },
    { name: 'Refurbished', value: 25 },
    { name: 'Refilled', value: 10 },
    { name: 'Incinerated', value: 5 }
  ];

  const milestones = [
    { title: 'Carbon Offset Goal', current: summaryData.carbonOffset, target: 15000, icon: Leaf },
    { title: 'Recycling Target', current: summaryData.itemsRecycled, target: 5000, icon: Recycle },
    { title: 'Water Conservation', current: summaryData.waterSaved, target: 50000, icon: Droplet },
    { title: 'Energy Savings', current: summaryData.energySaved, target: 30000, icon: Zap }
  ];

  return (
    <Container>
      <Section>
        <Stack space={6}>
          <div>
            <Text variant="h1">Sustainability Metrics</Text>
            <Text variant="body" className="mt-2 text-muted-foreground">Track your environmental impact and sustainability goals</Text>
          </div>

          <Grid cols={1} mdCols={2} lgCols={4} gap={6}>
            {milestones.map((milestone, index) => {
              const progress = (milestone.current / milestone.target) * 100;
              const Icon = milestone.icon;
              return (
                <Card key={index}>
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium">{milestone.title}</CardTitle>
                    <Icon className="h-4 w-4 text-muted-foreground" />
                  </CardHeader>
                  <CardContent>
                    <Stack space={1}>
                      <Text className="text-2xl font-bold">
                        {milestone.current.toLocaleString()}
                      </Text>
                      <div className="flex items-center gap-2">
                        <div className="h-2 flex-1 bg-secondary rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary rounded-full transition-all"
                            style={{ width: `${Math.min(progress, 100)}%` }}
                          />
                        </div>
                        <Text variant="body" className="text-sm text-muted-foreground">
                          {Math.round(progress)}%
                        </Text>
                      </div>
                      <Text variant="body" className="text-xs text-muted-foreground">
                        Target: {milestone.target.toLocaleString()}
                      </Text>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Grid>

          <Grid cols={1} mdCols={2} gap={6}>
            <Card>
              <CardHeader>
                <CardTitle>Carbon Savings Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <AreaChart
                  data={carbonSavingsData}
                  index="date"
                  categories={["savings"]}
                  colors={["emerald"]}
                  valueFormatter={(value) => `${value}kg CO₂`}
                  showLegend={false}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Material Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <DonutChart
                  data={materialBreakdownData}
                  category="value"
                  index="name"
                  valueFormatter={(value) => `${value}%`}
                  colors={["emerald", "blue", "amber", "violet", "slate"]}
                />
              </CardContent>
            </Card>
          </Grid>

          <Card>
            <CardHeader>
              <CardTitle>Disposition Analysis</CardTitle>
            </CardHeader>
            <CardContent>
              <BarChart
                data={dispositionData}
                index="name"
                categories={["value"]}
                colors={["emerald"]}
                valueFormatter={(value) => `${value}%`}
                showLegend={false}
              />
            </CardContent>
          </Card>
        </Stack>
      </Section>
    </Container>
  );
} 
